import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [pagination] - Pagination container.
 * @property {HTMLButtonElement} [loadMoreProducts] - Manual load more button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;
  /** @type {boolean} */
  #isLoadingNextPage = false;
  /** @type {boolean} */
  #isLoadingPreviousPage = false;
  /** @type {number | null} */
  #lastPreviousLoadScrollY = null;
  /** @type {number} */
  #maxCardsInDom = 72;
  /** @type {number} */
  #maxCardsInDomManual = 0;
  /** @type {number} */
  #infiniteAutoLoadLimit = 0;
  /** @type {boolean} */
  #autoLoadLimitReached = false;
  /** @type {boolean} */
  #isPaginating = false;
  /** @type {boolean} */
  #isConstrainedIOSWebKit = false;
  /** @type {boolean} */
  #prefetchEnabled = true;
  /** @type {number} */
  #scrollAnchorCorrectionToken = 0;
  /** @type {number} */
  #autoLoadedItemsInCycle = 0;
  /** @type {number} */
  #loadingMinVisibleMs = 220;
  /** @type {number} */
  #nextLoadingVisibleSince = 0;
  /** @type {number | null} */
  #nextLoadingHideTimeoutId = null;
  /** @type {number} */
  #previousLoadingVisibleSince = 0;
  /** @type {number | null} */
  #previousLoadingHideTimeoutId = null;
  /** @type {number} */
  #pageRequestGeneration = 0;
  /** @type {() => void} */
  #handleViewportResize = () => {
    this.#syncViewportInlineSize();
  };

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#maxCardsInDom = this.#getPositiveIntegerFromAttribute('data-max-cards-in-dom') || this.#maxCardsInDom;
    this.#maxCardsInDomManual = this.#getPositiveIntegerFromAttribute('data-max-cards-in-dom-manual');
    this.#infiniteAutoLoadLimit = this.#isInfiniteDisabled()
      ? 0
      : this.#getPositiveIntegerFromAttribute('data-infinite-auto-load-limit');
    this.#applyConstrainedIOSProfile();
    this.#resetAutoLoadCycle({ includeVisibleCards: true });
    this.#updateAutoLoadLimitState();
    this.#updateLoadMoreButtonVisibility();
    this.#syncViewportInlineSize();

    if (!this.#isInfiniteDisabled()) {
      if (this.#canAutoLoadNextPage() && this.#prefetchEnabled) {
        this.#fetchPage('next');
      }
      if (!this.#isPreviousLoadDisabled() && this.#prefetchEnabled) {
        this.#fetchPage('previous');
      }
    }
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('popstate', this.#handlePopState);
    window.addEventListener('resize', this.#handleViewportResize, { passive: true });
    window.visualViewport?.addEventListener('resize', this.#handleViewportResize);
  }

  #isInfiniteDisabled() {
    return this.hasAttribute('disable-infinite');
  }

  #isPreviousLoadDisabled() {
    return this.hasAttribute('disable-previous-load') || this.#isSafariWebKit();
  }

  #getPositiveIntegerFromAttribute(attributeName) {
    const rawValue = this.getAttribute(attributeName);
    if (!rawValue) return 0;

    const value = Number.parseInt(rawValue, 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  #isIOSWebKit() {
    const ua = window.navigator.userAgent || '';
    const isIOSDevice =
      /iP(hone|ad|od)/.test(ua) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const isAppleWebKit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIOSDevice && isAppleWebKit;
  }

  #isSafariWebKit() {
    const ua = window.navigator.userAgent || '';
    return /Safari/.test(ua) && /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium/.test(ua);
  }

  #applyConstrainedIOSProfile() {
    if (this.#isInfiniteDisabled() || !this.#isIOSWebKit()) return;

    this.#isConstrainedIOSWebKit = true;
    // iOS Safari is much more sensitive to DOM/cache growth on long sessions.
    this.#prefetchEnabled = false;
    this.#maxCardsInDom = Math.min(this.#maxCardsInDom, 32);
    this.#maxCardsInDomManual =
      this.#maxCardsInDomManual > 0 ? Math.min(this.#maxCardsInDomManual, 48) : this.#maxCardsInDom;
    if (this.#infiniteAutoLoadLimit > 0) {
      this.#infiniteAutoLoadLimit = Math.min(this.#infiniteAutoLoadLimit, 24);
    }
  }

  #getVisibleCardsCount() {
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    return cards?.length || 0;
  }

  #getFirstVisibleCardPage() {
    const firstCard = this.refs.grid?.querySelector(':scope > [ref="cards[]"]');
    const firstPage = Number(firstCard?.dataset.page || '');
    return Number.isFinite(firstPage) ? firstPage : 1;
  }

  #getLastVisibleCardPage() {
    const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
    if (!cards?.length) return 1;
    const lastCard = cards[cards.length - 1];
    const lastPage = Number(lastCard?.dataset.page || '');
    return Number.isFinite(lastPage) ? lastPage : 1;
  }

  #syncViewportInlineSize() {
    const viewportWidth = document.documentElement?.clientWidth || window.innerWidth || 0;
    if (viewportWidth <= 0) return;

    this.style.setProperty('--results-viewport-inline-size', `${viewportWidth}px`);
  }

  /**
   * Gives the browser one paint opportunity after toggling loading UI.
   * Helps avoid cases where heavy DOM work starts before loader becomes visible.
   * @returns {Promise<void>}
   */
  #waitForLoaderPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  /**
   * Captures the first visible product card and its viewport offset.
   * Used to keep the viewport stable while mutating the grid.
   * @returns {{ element: Element, productId: string, page: string, top: number } | null}
   */
  #captureScrollAnchor() {
    const grid = this.refs.grid;
    if (!(grid instanceof HTMLElement)) return null;

    const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    if (!cards.length) return null;

    let anchor = cards.find((card) => card instanceof HTMLElement && card.getBoundingClientRect().bottom > 0);
    if (!(anchor instanceof HTMLElement)) {
      anchor = cards[0];
    }
    if (!(anchor instanceof HTMLElement)) return null;

    return {
      element: anchor,
      productId: anchor.dataset.productId || '',
      page: anchor.dataset.page || '',
      top: anchor.getBoundingClientRect().top,
    };
  }

  /**
   * Restores the captured anchor offset after DOM/layout changes.
   * Includes short follow-up corrections for late layout shifts.
   * @param {{ element: Element, productId: string, page: string, top: number } | null} anchor
   */
  #restoreScrollAnchor(anchor) {
    if (!anchor) return;
    const correctionToken = ++this.#scrollAnchorCorrectionToken;

    const resolveAnchorElement = () => {
      const grid = this.refs.grid;
      if (!(grid instanceof HTMLElement)) return null;

      const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
      if (!cards.length) return null;

      if (anchor.element instanceof Element && cards.includes(anchor.element)) {
        return anchor.element;
      }

      let anchorElement = cards.find(
        (card) => card.dataset.productId === anchor.productId && card.dataset.page === anchor.page
      );

      if (!anchorElement) {
        anchorElement = cards.find((card) => card.dataset.productId === anchor.productId);
      }

      if (anchorElement) return anchorElement;

      // Fallback: pick the card closest to the original anchor top.
      let closestCard = cards[0];
      let closestDistance = Number.POSITIVE_INFINITY;
      for (const card of cards) {
        const distance = Math.abs(card.getBoundingClientRect().top - anchor.top);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestCard = card;
        }
      }

      return closestCard || null;
    };

    const adjust = () => {
      if (correctionToken !== this.#scrollAnchorCorrectionToken) return;
      const anchorElement = resolveAnchorElement();
      if (!(anchorElement instanceof HTMLElement)) return;

      const delta = anchorElement.getBoundingClientRect().top - anchor.top;
      if (Math.abs(delta) < 0.5) return;

      window.scrollBy({ top: delta, behavior: 'auto' });
    };

    adjust();

    // Follow-up corrections for delayed layout shifts (swatches/image settle).
    let attempts = 3;
    const stabilize = () => {
      if (correctionToken !== this.#scrollAnchorCorrectionToken) return;
      adjust();
      attempts -= 1;
      if (attempts > 0) requestAnimationFrame(stabilize);
    };
    requestAnimationFrame(stabilize);

    // Extra passes for late layout shifts:
    // product-card internals (images/swatches/price blocks) can reflow shortly
    // after insertion, especially on slower devices. We re-apply anchor correction
    // on two delayed checkpoints to keep viewport position stable.
    const lateLayoutCorrectionDelays = [90, 180];
    for (const delayMs of lateLayoutCorrectionDelays) {
      setTimeout(adjust, delayMs);
    }
  }

  #updateLoadMoreButtonVisibility() {
    const btn = this.refs?.loadMoreProducts;
    if (!(btn instanceof HTMLButtonElement)) return;

    const lastPage = Number(this.refs.grid?.dataset.lastPage || '0');
    const lastVisiblePage = this.#getLastVisibleCardPage();
    const canLoadMore = lastVisiblePage < lastPage;
    const shouldUseManualLoadMore = this.#isInfiniteDisabled() || this.#autoLoadLimitReached;

    btn.style.display = canLoadMore && shouldUseManualLoadMore ? '' : 'none';
    btn.disabled = !canLoadMore || this.#isLoadingNextPage;
  }

  /**
   * Updates visual loading state for next-page loading.
   * @param {boolean} isLoading
   */
  #setNextLoadingState(isLoading) {
    const applyState = (enabled) => {
      this.toggleAttribute('loading-next', enabled);

      const btn = this.refs?.loadMoreProducts;
      if (!(btn instanceof HTMLButtonElement)) return;

      btn.toggleAttribute('aria-busy', enabled);
      btn.classList.toggle('is-loading', enabled);
    };

    const clearPendingHide = () => {
      if (this.#nextLoadingHideTimeoutId == null) return;
      window.clearTimeout(this.#nextLoadingHideTimeoutId);
      this.#nextLoadingHideTimeoutId = null;
    };

    if (isLoading) {
      clearPendingHide();
      this.#nextLoadingVisibleSince = performance.now();
      applyState(true);
      return;
    }

    const hasActiveLoader = this.hasAttribute('loading-next');
    const elapsed = performance.now() - this.#nextLoadingVisibleSince;
    const remainingVisibleMs = hasActiveLoader ? this.#loadingMinVisibleMs - elapsed : 0;

    if (remainingVisibleMs > 0) {
      clearPendingHide();
      this.#nextLoadingHideTimeoutId = window.setTimeout(() => {
        this.#nextLoadingHideTimeoutId = null;
        this.#nextLoadingVisibleSince = 0;
        applyState(false);
      }, remainingVisibleMs);
      return;
    }

    clearPendingHide();
    this.#nextLoadingVisibleSince = 0;
    applyState(false);
  }

  /**
   * Updates visual loading state for previous-page loading.
   * @param {boolean} isLoading
   */
  #setPreviousLoadingState(isLoading) {
    const applyState = (enabled) => {
      this.toggleAttribute('loading-previous', enabled);
    };

    const clearPendingHide = () => {
      if (this.#previousLoadingHideTimeoutId == null) return;
      window.clearTimeout(this.#previousLoadingHideTimeoutId);
      this.#previousLoadingHideTimeoutId = null;
    };

    if (isLoading) {
      clearPendingHide();
      this.#previousLoadingVisibleSince = performance.now();
      applyState(true);
      return;
    }

    const hasActiveLoader = this.hasAttribute('loading-previous');
    const elapsed = performance.now() - this.#previousLoadingVisibleSince;
    const remainingVisibleMs = hasActiveLoader ? this.#loadingMinVisibleMs - elapsed : 0;

    if (remainingVisibleMs > 0) {
      clearPendingHide();
      this.#previousLoadingHideTimeoutId = window.setTimeout(() => {
        this.#previousLoadingHideTimeoutId = null;
        this.#previousLoadingVisibleSince = 0;
        applyState(false);
      }, remainingVisibleMs);
      return;
    }

    clearPendingHide();
    this.#previousLoadingVisibleSince = 0;
    applyState(false);
  }

  #updateAutoLoadLimitState() {
    if (this.#isInfiniteDisabled() || this.#infiniteAutoLoadLimit <= 0) {
      this.#autoLoadLimitReached = false;
      return;
    }

    const lastPage = Number(this.refs.grid?.dataset.lastPage || '0');
    const lastVisiblePage = this.#getLastVisibleCardPage();
    const hasMorePages = lastVisiblePage < lastPage;

    this.#autoLoadLimitReached = hasMorePages && this.#autoLoadedItemsInCycle >= this.#infiniteAutoLoadLimit;
  }

  #canAutoLoadNextPage() {
    if (this.#isInfiniteDisabled() || this.#autoLoadLimitReached) return false;

    const lastPage = Number(this.refs.grid?.dataset.lastPage || '0');
    const lastVisiblePage = this.#getLastVisibleCardPage();
    return lastVisiblePage < lastPage;
  }

  /**
   * Starts a new auto-loading cycle.
   * @param {{ includeVisibleCards?: boolean }} [options]
   */
  #resetAutoLoadCycle({ includeVisibleCards = false } = {}) {
    this.#autoLoadedItemsInCycle = includeVisibleCards ? this.#getVisibleCardsCount() : 0;
    this.#autoLoadLimitReached = false;
  }

  /**
   * Keeps the number of cards in the DOM bounded to reduce Safari memory pressure.
   * Removes cards from the opposite side of navigation and preserves scroll position.
   * @param {'next' | 'previous'} direction
   */
  #trimGridWindow(direction, maxCardsInDom = this.#maxCardsInDom) {
    // Window trimming is the main source of scroll drift on desktop because
    // removing earlier cards can trigger full CSS-grid reflow.
    // Keep trimming only for constrained iOS Safari memory profile.
    if (!this.#isConstrainedIOSWebKit) return;

    if (maxCardsInDom <= 0) return;

    const cards = Array.isArray(this.refs.cards) ? this.refs.cards : [];
    if (!cards.length || cards.length <= maxCardsInDom) return;

    const removeCount = cards.length - maxCardsInDom;
    if (removeCount <= 0) return;

    if (direction === 'next') {
      const toRemove = cards.slice(0, removeCount);
      const anchor = cards[removeCount];
      const anchorTopBefore = anchor instanceof HTMLElement ? anchor.getBoundingClientRect().top : null;

      toRemove.forEach((card) => card.remove());

      if (anchor instanceof HTMLElement && anchorTopBefore != null) {
        const anchorTopAfter = anchor.getBoundingClientRect().top;
        const delta = anchorTopAfter - anchorTopBefore;
        if (delta !== 0) {
          window.scrollBy({ top: delta, behavior: 'auto' });
        }
      }
      return;
    }

    const toRemove = cards.slice(cards.length - removeCount);
    toRemove.forEach((card) => card.remove());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#nextLoadingHideTimeoutId != null) {
      window.clearTimeout(this.#nextLoadingHideTimeoutId);
      this.#nextLoadingHideTimeoutId = null;
    }
    if (this.#previousLoadingHideTimeoutId != null) {
      window.clearTimeout(this.#previousLoadingHideTimeoutId);
      this.#previousLoadingHideTimeoutId = null;
    }
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('resize', this.#handleViewportResize);
    window.visualViewport?.removeEventListener('resize', this.#handleViewportResize);
  }

  #observeViewMore() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Next is observed only while infinite auto-loading is active.
    const shouldObserveNext = this.#canAutoLoadNextPage() && Boolean(viewMoreNext);

    // Observe "previous" only when there are earlier pages to load.
    const shouldObservePrevious =
      !this.#isPreviousLoadDisabled() && Boolean(viewMorePrevious) && this.#getFirstVisibleCardPage() > 1;

    // If nothing to observe, return
    if (!shouldObservePrevious && !shouldObserveNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      const rootMargin = this.#isConstrainedIOSWebKit ? '24px' : '100px';
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                // Only render next if allowed (observer exists only when allowed)
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin,
        }
      );
    }

    // Observe the view more elements based on the computed flags
    if (shouldObservePrevious && viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (shouldObserveNext && viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };
    const requestGeneration = this.#pageRequestGeneration;

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, pageInfo.url);

    if (requestGeneration !== this.#pageRequestGeneration) return;

    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Public method to programmatically load the next page (used by Load More button)
   */// @public
  loadNextPage({ manual = false } = {}) {
    return this.#renderNextPage({ manual });
  }

  /** Click handler bound via declarative events to load more */
  handleLoadMoreClick(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.loadNextPage({ manual: true });
  }

  /**
   * Click handler for numbered pagination links.
   * Fetches and swaps grid/pagination without full page reload.
   *
   * @param {MouseEvent} event
   */
  async handlePaginationClick(event) {
    const link = event?.target;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    event.stopPropagation();

    const url = new URL(link.href, window.location.origin);
    await this.#navigateToUrl(url, { link, pushHistory: true, scrollToTop: false, preserveScroll: true });
  }

  #handlePopState = async () => {
    this.#pageRequestGeneration += 1;
    await this.#navigateToUrl(new URL(window.location.href), {
      pushHistory: false,
      scrollToTop: false,
      preserveScroll: true,
    });
  };

  /**
   * Fetch and replace collection page content while preserving scroll context.
   *
   * @param {URL} url
   * @param {{ link?: HTMLAnchorElement, pushHistory?: boolean, scrollToTop?: boolean, preserveScroll?: boolean }} [options]
   */
  async #navigateToUrl(url, { link, pushHistory = true, scrollToTop = false, preserveScroll = true } = {}) {
    if (this.#isPaginating) return;

    const displayUrl = new URL(url.toString());
    displayUrl.searchParams.delete('section_id');

    const pageParam = Number(displayUrl.searchParams.get('page') || '1');
    if (!Number.isFinite(pageParam) || pageParam < 1) return;

    this.#isPaginating = true;
    link?.setAttribute('aria-disabled', 'true');
    const scrollYBefore = window.scrollY;

    try {
      const requestUrl = new URL(displayUrl.toString());
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, requestUrl);
      const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
      const newGrid = parsedPage.querySelector('[ref="grid"]');

      if (!(newGrid instanceof HTMLElement)) return;

      const currentGrid = this.refs.grid;
      if (!(currentGrid instanceof HTMLElement)) return;

      const currentPagination = this.refs.pagination;
      const newPagination = parsedPage.querySelector('[ref="pagination"]');

      currentGrid.replaceWith(newGrid);

      if (currentPagination instanceof HTMLElement) {
        if (newPagination instanceof HTMLElement) {
          currentPagination.replaceWith(newPagination);
        } else {
          currentPagination.remove();
        }
      } else if (newPagination instanceof HTMLElement) {
        newGrid.insertAdjacentElement('afterend', newPagination);
      }

      this.#sanitizePaginationLinks();
      this.#reflowSwatchesOverflowLists();

      if (pushHistory) {
        history.pushState('', '', displayUrl.toString());
      }

      if (scrollToTop) {
        const listStart = this.querySelector('#ResultsList');
        if (listStart instanceof HTMLElement) {
          const top = listStart.getBoundingClientRect().top + window.scrollY - this.#getHeaderOffset();
          window.scrollTo({ top, behavior: 'auto' });
        }
      } else if (preserveScroll) {
        window.scrollTo({ top: scrollYBefore, behavior: 'auto' });
      }

      this.pages.clear();
      this.#pageRequestGeneration += 1;
    } catch {
      if (pushHistory) {
        window.location.assign(displayUrl.toString());
      }
    } finally {
      link?.removeAttribute('aria-disabled');
      this.#isPaginating = false;
    }
  }

  #sanitizePaginationLinks() {
    const pagination = this.refs.pagination;
    if (!(pagination instanceof HTMLElement)) return;

    const links = pagination.querySelectorAll('a[href]');
    for (const anchor of links) {
      try {
        const href = anchor.getAttribute('href');
        if (!href) continue;

        const linkUrl = new URL(href, window.location.origin);
        linkUrl.searchParams.delete('section_id');
        anchor.setAttribute('href', `${linkUrl.pathname}${linkUrl.search}${linkUrl.hash}`);
      } catch {
        // Ignore invalid URLs.
      }
    }
  }

  /**
   * Forces swatches overflow lists to recalculate after dynamic grid updates.
   * This avoids stale "+N" counters when card layout settles after pagination.
   */
  #reflowSwatchesOverflowLists() {
    const overflowLists = this.querySelectorAll('overflow-list');
    if (!overflowLists.length) return;

    const triggerReflow = () => {
      for (const overflowList of overflowLists) {
        overflowList.dispatchEvent(
          new CustomEvent('reflow', {
            bubbles: true,
            detail: {},
          })
        );
      }
    };

    triggerReflow();
    requestAnimationFrame(triggerReflow);
    if (!this.#isSafariWebKit()) {
      setTimeout(triggerReflow, 120);
    }
  }

  #getHeaderOffset() {
    const styles = getComputedStyle(document.body);
    const headerHeight = Number.parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const headerGroupHeight = Number.parseFloat(styles.getPropertyValue('--header-group-height')) || 0;
    const headerOffset = Math.max(headerHeight, headerGroupHeight);
    const filtersOffset = this.#getFiltersOffset();

    // Small breathing room below sticky elements.
    return headerOffset + filtersOffset + 8;
  }

  #getFiltersOffset() {
    const candidates = [
      '.facets-controls-wrapper',
      '.facets-block-wrapper--horizontal .facets',
      '.facets-toggle',
    ];

    let maxHeight = 0;
    for (const selector of candidates) {
      const element = this.querySelector(selector);
      if (!(element instanceof HTMLElement)) continue;

      const style = getComputedStyle(element);
      const isHidden =
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        element.offsetParent === null ||
        element.classList.contains('hidden');

      if (isHidden) continue;

      maxHeight = Math.max(maxHeight, element.getBoundingClientRect().height);
    }

    return maxHeight;
  }

  async #renderNextPage({ manual = false } = {}) {
    if (this.#isLoadingNextPage) return;

    this.#isLoadingNextPage = true;
    const { grid } = this.refs;

    if (!grid) {
      this.#setNextLoadingState(false);
      this.#isLoadingNextPage = false;
      return;
    }

    const btn = /** @type {HTMLButtonElement | undefined} */ (this.refs?.loadMoreProducts);
    if (btn) btn.disabled = true;
    this.#setNextLoadingState(true);
    await this.#waitForLoaderPaint();
    const scrollAnchor = this.#captureScrollAnchor();
    const previousOverflowAnchor = grid.style.overflowAnchor;
    grid.style.overflowAnchor = 'none';

    try {
      const nextPage = this.#getPage('next');
      if (!nextPage || !this.#shouldUsePage(nextPage)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      grid.append(...nextPageItemElements);
      this.pages.delete(nextPage.page);
      const appendedItemsCount = nextPageItemElements.length || 0;

      this.#aspectRatioHelper.processNewElements();
      const trimLimit = manual && this.#maxCardsInDomManual > 0 ? this.#maxCardsInDomManual : this.#maxCardsInDom;
      this.#trimGridWindow('next', trimLimit);
      this.#reflowSwatchesOverflowLists();

      history.replaceState(history.state, '', nextPage.url.toString());
      this.#restoreScrollAnchor(scrollAnchor);

      if (manual) {
        // After explicit "Show more", re-enable infinite auto-loading for a new batch.
        this.#resetAutoLoadCycle();
      } else {
        this.#autoLoadedItemsInCycle += appendedItemsCount;
      }

      this.#updateAutoLoadLimitState();
      this.#updateLoadMoreButtonVisibility();
      this.#observeViewMore();

      if (this.#canAutoLoadNextPage() && this.#prefetchEnabled) {
        requestIdleCallback(() => {
          this.#fetchPage('next');
        });
      }
    } finally {
      grid.style.overflowAnchor = previousOverflowAnchor;
      this.#setNextLoadingState(false);
      this.#isLoadingNextPage = false;
      this.#updateAutoLoadLimitState();
      this.#updateLoadMoreButtonVisibility();
    }
  }

  async #renderPreviousPage() {
    if (this.#isLoadingPreviousPage) return;

    // Prevent repeated auto-loads while observer stays intersecting near top.
    if (
      this.#lastPreviousLoadScrollY != null &&
      Math.abs(window.scrollY - this.#lastPreviousLoadScrollY) < 48
    ) {
      return;
    }

    this.#isLoadingPreviousPage = true;
    this.#setPreviousLoadingState(true);
    await this.#waitForLoaderPaint();

    const { grid } = this.refs;

    if (!grid) {
      this.#setPreviousLoadingState(false);
      this.#isLoadingPreviousPage = false;
      return;
    }
    const scrollAnchor = this.#captureScrollAnchor();
    const previousOverflowAnchor = grid.style.overflowAnchor;
    grid.style.overflowAnchor = 'none';

    try {
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);
      this.pages.delete(previousPage.page);

      this.#aspectRatioHelper.processNewElements();
      this.#trimGridWindow('previous');
      this.#reflowSwatchesOverflowLists();
      this.#updateAutoLoadLimitState();
      this.#updateLoadMoreButtonVisibility();
      this.#observeViewMore();

      history.replaceState(history.state, '', previousPage.url.toString());
      this.#restoreScrollAnchor(scrollAnchor);

      this.#lastPreviousLoadScrollY = window.scrollY;
      if (this.#prefetchEnabled) {
        requestIdleCallback(() => {
          this.#fetchPage('previous');
        });
      }
    } finally {
      grid.style.overflowAnchor = previousOverflowAnchor;
      this.#setPreviousLoadingState(false);
      this.#isLoadingPreviousPage = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#pageRequestGeneration += 1;
    this.pages.clear();
    this.#lastPreviousLoadScrollY = null;
    this.#resetAutoLoadCycle();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const refreshPaginationState = () => {
      // Check if component is still connected
      if (!this.isConnected) {
        return;
      }

      // Now the DOM has been updated with the new filtered/sorted content.
      this.#resetAutoLoadCycle({ includeVisibleCards: true });
      this.#updateAutoLoadLimitState();
      this.#observeViewMore();
      this.#updateLoadMoreButtonVisibility();

      // Fetch next page in background only for infinite mode.
      if (this.#canAutoLoadNextPage() && this.#prefetchEnabled) {
        this.#fetchPage('next');
      }
    };

    // We need to wait for the DOM to be updated with the new filtered/sorted content
    // Using mutation observer to detect when the grid actually updates
    let didRefresh = false;
    const refreshOnce = () => {
      if (didRefresh) return;
      didRefresh = true;
      observer.disconnect();
      refreshPaginationState();
    };

    const observer = new MutationObserver(() => {
      requestAnimationFrame(refreshOnce);
    });

    // Observe this component so we also catch a full grid replacement.
    const { grid } = this.refs;
    if (grid) {
      observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
        subtree: true,
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        refreshOnce();
      }, 3000);
    }
  };
}
